import {NumberInput, useTranslationContext} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

// eslint-disable-next-line no-restricted-imports -- Intended
import {isNil, isNotNil, path} from 'ramda';
import {isString, toNumber} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {FILTER_VALUE_KEY} from '../../constants/filterValueKey';
import type {EagFilterProps} from '../../types/EagFilterProps';
import {getQuickFilterFromString} from '../../utils/quickFilterValue';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';

type RangeFilterType = EagFilterProps & {
  quickFilters?: Array<{value: string; label: string}>;
};

type RangeObject = {from: number | null; to: number | null};
type FilterValue = RangeObject | string;

export function RangeFilter(props: RangeFilterType) {
  const t = useTranslationContext();

  const filterValue = props.value[FILTER_VALUE_KEY] as FilterValue | null;

  const setFilterValue = (value: FilterValue | null) => {
    props.onChange({[FILTER_VALUE_KEY]: value});
  };

  const onFromInputChange = (value: number | null) => {
    const toValue = (filterValue as RangeObject | null)?.to;

    // Symmetric with onToInputChange: to === 0 maps to null below, so clearing
    // `from` while to is 0 must clear the whole filter, not store {from: null,
    // to: null} (an all-nil object that kept the filter active).
    if (typeof filterValue !== 'string' && isNil(value) && (isNil(toValue) || toValue === 0)) {
      props.onChange({[FILTER_VALUE_KEY]: null});
      return;
    }

    if (isString(filterValue) || isNil(filterValue)) {
      setFilterValue({from: toNumber(value), to: null});
    }

    if (!isString(filterValue)) {
      setFilterValue({
        from: isNil(value) ? value : toNumber(value),
        to:
          (filterValue as RangeObject | null)?.to === 0
            ? null
            : ((filterValue as RangeObject | null)?.to ?? null),
      });
    }
  };

  const onToInputChange = (value: number | null) => {
    const fromValue = (filterValue as RangeObject | null)?.from;

    if (typeof filterValue !== 'string' && isNil(value) && (isNil(fromValue) || fromValue === 0)) {
      props.onChange({[FILTER_VALUE_KEY]: null});
      return;
    }

    if (isString(filterValue) || isNil(filterValue)) {
      setFilterValue({to: toNumber(value), from: null});
    }

    if (!isString(filterValue)) {
      setFilterValue({
        from: fromValue === 0 ? null : (fromValue ?? null),
        to: isNil(value) ? value : toNumber(value),
      });
    }
  };

  const isQuickFilterValue = getQuickFilterFromString(filterValue);

  return (
    <VStack spacing={2}>
      {/* Deselect clears the filter entirely (null) — restoring {from: null,
          to: null} kept it "active" and rendered a literal "To null" chip. */}
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={isQuickFilterValue}
        defaultValue={null}
        setFilterValue={setFilterValue}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('range-filter-quickFilters', props)}
      />
      <HStack spacing={5}>
        <NumberInput
          label={t('general.labels.from')}
          value={hasValidKey(filterValue, 'from') ? toNumber(filterValue.from) : null}
          onChange={onFromInputChange}
          isDisabled={props.isDisabled}
          data-testid={suffixTestId('range-filter-from', props)}
        />
        <NumberInput
          label={t('general.labels.to')}
          value={hasValidKey(filterValue, 'to') ? toNumber(filterValue.to) : null}
          onChange={onToInputChange}
          isDisabled={props.isDisabled}
          data-testid={suffixTestId('range-filter-to', props)}
        />
      </HStack>
    </VStack>
  );
}

const hasValidKey = <T extends 'from' | 'to'>(val: unknown, key: T): val is Record<T, string> =>
  isNotNil(path([key], val));
